import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  deleteDoc,
  getDocs,
  setDoc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { useAuth } from '../contexts/authContext';
import {
  DEFAULT_PRESET_ID,
  MAX_USER_PRESETS,
  PRESET_SCHEMA_VERSION,
  getDefaultPresetEntry,
} from '../components/map-gl/presets/defaultPreset';
import {
  presetFromFirestore,
  presetToFirestore,
} from '../components/map-gl/presets/presetSerialization';

const ACTIVE_PRESET_LOCALSTORAGE_KEY = 'goldfinder.activePresetId';

function readActiveIdFromStorage() {
  try {
    return localStorage.getItem(ACTIVE_PRESET_LOCALSTORAGE_KEY) || DEFAULT_PRESET_ID;
  } catch {
    return DEFAULT_PRESET_ID;
  }
}

function writeActiveIdToStorage(id) {
  try {
    localStorage.setItem(ACTIVE_PRESET_LOCALSTORAGE_KEY, id);
  } catch {
    // ignore
  }
}

function sortDesc(presets) {
  return [...presets].sort((a, b) => {
    const at = a.updatedAt || '';
    const bt = b.updatedAt || '';
    if (at < bt) return 1;
    if (at > bt) return -1;
    return 0;
  });
}

export function useOverlayPresets() {
  const { currentUser, userLoggedIn } = useAuth();

  const [userPresets, setUserPresets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePresetId, setActivePresetIdState] = useState(() => readActiveIdFromStorage());

  const defaultEntry = useMemo(() => getDefaultPresetEntry(), []);
  const presets = useMemo(
    () => [defaultEntry, ...sortDesc(userPresets)],
    [defaultEntry, userPresets]
  );

  const setActivePresetId = useCallback((id) => {
    setActivePresetIdState(id);
    writeActiveIdToStorage(id);
  }, []);

  const getPresetById = useCallback(
    (id) => presets.find((p) => p.id === id),
    [presets]
  );

  const presetsCollectionRef = useMemo(() => {
    if (!userLoggedIn || !currentUser?.uid) return null;
    return collection(db, 'user_info', currentUser.uid, 'overlay_presets');
  }, [userLoggedIn, currentUser]);

  // Load presets on auth change
  useEffect(() => {
    let cancelled = false;

    async function load() {
      setIsLoading(true);
      setError(null);

      if (!presetsCollectionRef) {
        if (!cancelled) {
          setUserPresets([]);
          setIsLoading(false);
        }
        return;
      }

      try {
        const snap = await getDocs(presetsCollectionRef);
        const loaded = [];
        snap.forEach((docSnap) => {
          try {
            loaded.push(presetFromFirestore(docSnap));
          } catch (e) {
            console.warn('Failed to deserialize preset', docSnap.id, e);
          }
        });
        if (!cancelled) setUserPresets(loaded);
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [presetsCollectionRef]);

  const savePresetAsNew = useCallback(
    async (name, payload) => {
      if (!presetsCollectionRef) {
        throw new Error('You must be signed in to save presets.');
      }
      if (userPresets.length >= MAX_USER_PRESETS) {
        throw new Error(`Preset limit (${MAX_USER_PRESETS}) reached.`);
      }
      const newDocRef = doc(presetsCollectionRef);
      const record = presetToFirestore({
        name,
        createdAt: null,
        payload,
      });
      await setDoc(newDocRef, record);
      const entry = {
        id: newDocRef.id,
        name: record.name,
        version: record.version,
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
        payload,
        isBuiltIn: false,
      };
      setUserPresets((prev) => [...prev, entry]);
      return newDocRef.id;
    },
    [presetsCollectionRef, userPresets.length]
  );

  const updatePreset = useCallback(
    async (id, payload) => {
      if (!presetsCollectionRef) {
        throw new Error('You must be signed in to update presets.');
      }
      const existing = userPresets.find((p) => p.id === id);
      if (!existing) throw new Error('Preset not found.');
      const record = presetToFirestore({
        name: existing.name,
        createdAt: existing.createdAt,
        payload,
      });
      const docRef = doc(presetsCollectionRef, id);
      await setDoc(docRef, record);
      setUserPresets((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
                ...p,
                version: record.version,
                updatedAt: record.updatedAt,
                payload,
              }
            : p
        )
      );
    },
    [presetsCollectionRef, userPresets]
  );

  const renamePreset = useCallback(
    async (id, newName) => {
      if (!presetsCollectionRef) {
        throw new Error('You must be signed in to rename presets.');
      }
      const updatedAt = new Date().toISOString();
      const docRef = doc(presetsCollectionRef, id);
      await updateDoc(docRef, { name: newName, updatedAt });
      setUserPresets((prev) =>
        prev.map((p) => (p.id === id ? { ...p, name: newName, updatedAt } : p))
      );
    },
    [presetsCollectionRef]
  );

  const deletePreset = useCallback(
    async (id) => {
      if (!presetsCollectionRef) {
        throw new Error('You must be signed in to delete presets.');
      }
      const docRef = doc(presetsCollectionRef, id);
      await deleteDoc(docRef);
      setUserPresets((prev) => prev.filter((p) => p.id !== id));
      setActivePresetIdState((curr) => {
        if (curr === id) {
          writeActiveIdToStorage(DEFAULT_PRESET_ID);
          return DEFAULT_PRESET_ID;
        }
        return curr;
      });
    },
    [presetsCollectionRef]
  );

  const canSaveMore = userPresets.length < MAX_USER_PRESETS;

  return {
    presets,
    activePresetId,
    setActivePresetId,
    isLoading,
    error,
    savePresetAsNew,
    updatePreset,
    renamePreset,
    deletePreset,
    getPresetById,
    canSaveMore,
    userLoggedIn,
    userPresetCount: userPresets.length,
    maxUserPresets: MAX_USER_PRESETS,
    schemaVersion: PRESET_SCHEMA_VERSION,
  };
}
